<template>
  <div>
    <app-header title="">
      <template v-slot:actions>
        <button class="btn-add" v-c-modal:tentativeModal="{}">
          + Add Tentative
        </button>
      </template>
    </app-header>

    <div class="forecast-page">
      <!-- Top Bar -->
      <div class="topbar">
        <div class="topbar-left">
          <h1 class="topbar-title">Invoice Cashflow Forecast</h1>
          <span class="fy-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            FY {{ selectedYear - 1 }}/{{ selectedYear.toString().slice(2) }}
          </span>
        </div>
        <div class="topbar-right">
          <multiselect
            :show-labels="false"
            v-model="selectedClient"
            :options="clientList"
            label="company"
            track-by="id"
            placeholder="All Clients"
            :allow-empty="true"
            @select="onClientSelect"
            @remove="onClientRemove"
            class="client-multiselect"
          ></multiselect>
          <select class="category-select" v-model="selectedCategory" @change="loadForecast()">
            <option :value="null">All</option>
            <option v-for="cat in categories" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
          </select>
          <div class="fy-switcher">
            <button
              v-for="year in yearOptions"
              :key="year"
              :class="{ active: year === selectedYear }"
              @click="selectedYear = year; loadForecast()"
            >
              {{ (year - 1).toString().slice(2) }}/{{ year.toString().slice(2) }}
            </button>
          </div>
        </div>
      </div>

      <!-- KPI Bar -->
      <div class="kpi-bar" v-if="!loading">
        <div class="kpi-item">
          <div class="kpi-accent" style="background: #4057df;"></div>
          <div class="kpi-label">Year Target</div>
          <div class="kpi-value">{{ formatCurrency(yearTarget) }}</div>
          <div class="kpi-sub">{{ formatCurrencyCompact(monthlyTarget) }} / month</div>
        </div>
        <div class="kpi-item kpi-hoverable">
          <div class="kpi-accent" style="background: #6c7ae0;"></div>
          <div class="kpi-label">Total Forecast</div>
          <div class="kpi-value" style="color:#4057df;">{{ formatCurrency(totalForecast) }}</div>
          <div class="kpi-progress"><div class="kpi-progress-fill" :style="{ width: forecastPct + '%', background: 'linear-gradient(90deg,#4057df,#6c7ae0)' }"></div></div>
          <div class="kpi-sub">{{ forecastPct }}% of target</div>
          <div class="kpi-tooltip">Sum of all opportunity instalments, SLAs, retainers, T&amp;M budgets, DevOps and tentative entries across the financial year. Linked invoices are excluded to avoid double-counting.</div>
        </div>
        <div class="kpi-item kpi-hoverable">
          <div class="kpi-accent" style="background: #34c38f;"></div>
          <div class="kpi-label">Raised</div>
          <div class="kpi-value" style="color:#34c38f;">{{ formatCurrency(totalRaised) }}</div>
          <div class="kpi-progress"><div class="kpi-progress-fill" :style="{ width: raisedPct + '%', background: '#34c38f' }"></div></div>
          <div class="kpi-sub">{{ raisedPct }}% of target</div>
          <div class="kpi-tooltip">Total revenue from all invoices raised across opportunities, SLAs, retainers, T&amp;M and DevOps. Excludes retainer-linked opportunities.</div>
        </div>
        <div class="kpi-item kpi-hoverable">
          <div class="kpi-accent" style="background: #8b5cf6;"></div>
          <div class="kpi-label">Pipeline</div>
          <div class="kpi-value" style="color:#8b5cf6;">{{ formatCurrency(totalPipeline) }}</div>
          <div class="kpi-progress"><div class="kpi-progress-fill" :style="{ width: pipelinePct + '%', background: '#8b5cf6' }"></div></div>
          <div class="kpi-sub">Signed + Quoted</div>
          <div class="kpi-tooltip">Predicted instalments from Signed, Quoted and Commitment opportunities that haven't been invoiced yet, plus tentative entries. Excludes retainer-linked opportunities.</div>
        </div>
        <div class="kpi-item kpi-hoverable">
          <div class="kpi-accent" style="background: #8b5cf6;"></div>
          <div class="kpi-label">Recurring</div>
          <div class="kpi-value" style="color:#8b5cf6;">{{ formatCurrency(totalRecurring) }}</div>
          <div class="kpi-progress"><div class="kpi-progress-fill" :style="{ width: recurringPct + '%', background: '#8b5cf6' }"></div></div>
          <div class="kpi-sub">{{ recurringPct }}% of target</div>
          <div class="kpi-tooltip">Total forecast from recurring revenue streams: SLAs, retainers, T&amp;M budgets and DevOps.</div>
        </div>
        <div class="kpi-item kpi-hoverable">
          <div class="kpi-accent" style="background: #ff6b6b;"></div>
          <div class="kpi-label">Gap to Target</div>
          <div class="kpi-value" style="color:#ff6b6b;">{{ formatCurrency(yearRemaining > 0 ? yearRemaining : 0) }}</div>
          <div class="kpi-progress"><div class="kpi-progress-fill" :style="{ width: gapPct + '%', background: '#ff6b6b' }"></div></div>
          <div class="kpi-sub">{{ gapPct }}% unfilled</div>
          <div class="kpi-tooltip">Year Target minus Total Forecast. The remaining revenue needed to hit the annual target.</div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-wrapper">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <!-- Chart -->
      <forecast-chart
        v-if="!loading && months.length"
        :months="months"
        :breakdown="chartBreakdown"
        :targets="targets"
        :totals="totals"
      />

      <!-- Grid -->
      <forecast-grid
        v-if="!loading"
        :grouped-rows="groupedRows"
        :months="months"
        :totals="totals"
        :targets="targets"
        :remaining="monthlyRemaining"
        :section-totals="sectionTotals"
        :grand-total="grandTotal"
        :year-target="yearTarget"
        :year-remaining="yearRemaining"
        @update-name="onUpdateName"
        @update-month="onUpdateMonth"
        @remove-month="onRemoveMonth"
        @delete-row="onDeleteRow"
      />

      <tentative-form ref="tentativeModal"></tentative-form>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import Multiselect from 'vue-multiselect'
import ForecastGrid from './ForecastGrid.vue'
import ForecastChart from './ForecastChart.vue'
import TentativeForm from './TentativeForm.vue'

export default {
  components: { ForecastGrid, ForecastChart, TentativeForm, Multiselect },

  data() {
    return {
      selectedYear: this.getCurrentFY(),
      selectedCategory: null,
      selectedClient: null
    }
  },

  computed: {
    ...mapState('forecast', ['rows', 'months', 'totals', 'targets', 'kpis', 'categories', 'loading', 'sectionTotals', 'chartBreakdown', 'grandTotal', 'yearTarget', 'yearRemaining']),
    ...mapGetters('forecast', ['groupedRows', 'monthlyRemaining']),
    ...mapState('clientsList', { clientList: 'clients' }),

    monthlyTarget() {
      const vals = Object.values(this.targets)
      return vals.length ? vals[0] : 0
    },

    totalForecast() {
      return this.kpis.total_forecast || 0
    },

    totalRaised() {
      return this.kpis.total_raised || 0
    },

    totalRecurring() {
      return this.kpis.total_recurring || 0
    },

    totalPipeline() {
      return this.kpis.total_pipeline || 0
    },

    forecastPct() {
      return this.yearTarget > 0 ? Math.round((this.totalForecast / this.yearTarget) * 100) : 0
    },

    raisedPct() {
      return this.yearTarget > 0 ? Math.round((this.totalRaised / this.yearTarget) * 100) : 0
    },

    recurringPct() {
      return this.yearTarget > 0 ? Math.round((this.totalRecurring / this.yearTarget) * 100) : 0
    },

    pipelinePct() {
      return this.yearTarget > 0 ? Math.round((this.totalPipeline / this.yearTarget) * 100) : 0
    },

    gapPct() {
      return this.yearTarget > 0 ? Math.round((Math.max(0, this.yearRemaining) / this.yearTarget) * 100) : 0
    },

    yearOptions() {
      const current = this.getCurrentFY()
      return [current - 1, current, current + 1]
    }
  },

  mounted() {
    this.$store.dispatch('clientsList/getClients')
    this.loadForecast()
  },

  methods: {
    ...mapActions('forecast', ['fetchForecast']),

    getCurrentFY() {
      const now = new Date()
      return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear()
    },

    loadForecast(silent = false) {
      this.$store.dispatch('forecast/fetchForecast', { year: this.selectedYear, category: this.selectedCategory, clientId: this.selectedClient ? this.selectedClient.id : null, silent })
    },

    onClientSelect(client) {
      this.selectedClient = client
      this.loadForecast()
    },

    onClientRemove() {
      this.selectedClient = null
      this.loadForecast()
    },

    formatCurrency(cents) {
      const dollars = cents / 100
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(dollars)
    },

    formatCurrencyCompact(cents) {
      const dollars = cents / 100
      if (dollars >= 1000000) return '$' + (dollars / 1000000).toFixed(1) + 'M'
      if (dollars >= 1000) return '$' + Math.round(dollars / 1000) + 'K'
      return '$' + dollars
    },

    async onUpdateName({ entryId, name }) {
      await this.$store.dispatch('forecast/updateTentativeRowName', { entryId, name })
    },

    async onUpdateMonth({ entryId, month, amount }) {
      await this.$store.dispatch('forecast/updateTentativeMonth', { entryId, month, amount })
      this.loadForecast(true)
    },

    async onRemoveMonth({ entryId, monthId }) {
      await this.$store.dispatch('forecast/removeTentativeMonth', { entryId, monthId })
      this.loadForecast(true)
    },

    async onDeleteRow(entryId) {
      await this.$store.dispatch('forecast/removeTentativeRow', entryId)
    }
  }
}
</script>

<style scoped>
.forecast-page {
  padding: 24px 32px;
}

/* Top bar */
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}
.topbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.topbar-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e1e2d;
  letter-spacing: -0.3px;
  margin: 0;
}
.fy-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  background: #eef0ff;
  color: #4057df;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
}
.topbar-right {
  display: flex;
  gap: 10px;
  align-items: center;
}
.fy-switcher {
  display: flex;
  background: #fff;
  border: 1px solid #e2e5f0;
  border-radius: 8px;
  overflow: hidden;
}
.fy-switcher button {
  padding: 7px 16px;
  border: none;
  background: transparent;
  color: #8b8fa7;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.fy-switcher button:hover {
  color: #4057df;
}
.fy-switcher button.active {
  background: #4057df;
  color: #fff;
}
.btn-add {
  padding: 7px 18px;
  background: #4057df;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-add:hover {
  background: #3347c7;
}
.category-select {
  padding: 7px 14px;
  border: 1px solid #e2e5f0;
  border-radius: 8px;
  background: #fff;
  color: #1e1e2d;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
}
.category-select:focus {
  border-color: #4057df;
}
.client-multiselect {
  width: 240px;
}

/* KPI Bar */
.kpi-bar {
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid #e8eaf2;
}
.kpi-item {
  flex: 1;
  padding: 18px 24px;
  border-right: 1px solid #f0f1f7;
  position: relative;
}
.kpi-item:last-child {
  border-right: none;
}
.kpi-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
}
.kpi-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #8b8fa7;
  font-weight: 600;
  margin-bottom: 4px;
}
.kpi-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e1e2d;
}
.kpi-sub {
  font-size: 0.72rem;
  color: #a0a4b8;
  margin-top: 2px;
}
.kpi-progress {
  height: 4px;
  background: #f0f1f7;
  border-radius: 2px;
  margin-top: 10px;
  overflow: hidden;
}
.kpi-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* KPI Tooltip */
.kpi-hoverable {
  cursor: default;
}
.kpi-tooltip {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  background: #1e1e2d;
  color: #d1d5e4;
  font-size: 0.72rem;
  line-height: 1.5;
  padding: 10px 14px;
  border-radius: 8px;
  width: 240px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}
.kpi-hoverable:hover .kpi-tooltip {
  display: block;
}

/* Loading */
.loading-wrapper {
  text-align: center;
  padding: 60px 0;
}
</style>
